/**
 * SlotCard — карточка одного типа Venture Slot на странице Slots.
 *
 * В ТЗ v3.0 слоты одного уровня можно открывать сколько угодно,
 * поэтому одна карточка теперь охватывает ВСЕ активные экземпляры
 * одного типа:
 *
 *   - Если ни одного не открыто:
 *       → state = 'open' (если месяц разблокирован) или 'locked'
 *       → CTA "Открыть контракт"
 *
 *   - Если есть открытые экземпляры:
 *       → Счётчик ×N в заголовке
 *       → Суммарный earned / суммарный лимит 2.5× (N × amount)
 *       → Прогресс бар по среднему состоянию
 *       → CTA "Открыть ещё 1"  (если месяц доступен)
 *       → Tap на тело карточки → SlotDetailsModal со списком всех экземпляров
 *
 *   - Если слот locked → приглушённый вид + unlock countdown
 *
 * Дизайн: крупная иконка WebP слева, Liquid Glass поверхность, hue-based accent.
 */
import { memo, useEffect, useState } from 'react';
import { Check, Flame, Plus, Zap, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VentureSlotInfo } from '@/data/ventureSlots';
import { useUISounds } from '@/hooks/useUISounds';

export type SlotState = 'locked' | 'open' | 'active';

export interface ActiveSlotInstance {
  /** Stable id per slot contract (from backend when integrated) */
  contractId: string;
  /** USDT already earned toward the 250% limit for THIS instance */
  earned: number;
  /** Days since THIS instance was opened */
  daysActive: number;
  /** Whether Plasma Core is active on THIS instance */
  plasmaCoreActive?: boolean;
}

interface SlotCardProps {
  slot: VentureSlotInfo;
  /** Whether the slot's month is unlocked and purchasable right now */
  unlocked: boolean;
  /** All owned instances of this slot type */
  owned?: ActiveSlotInstance[];
  /** ISO дата открытия (для live countdown). */
  unlockAt?: string;
  /** ДД.ММ.ГГГГ MSK — для статичного отображения «Откроется ...». */
  unlockAtMsk?: string;
  /** Показывать ли live countdown «До открытия: Xд Yч Zм». */
  showCountdown?: boolean;
  /** Fires when the user taps the main body (open details OR deposit) */
  onTapDetails?: () => void;
  /** Fires when the user taps the "open another" CTA */
  onTapBuy?: () => void;
  /** Fires when user wants to buy PC for all contracts without PC */
  onTapPC?: (contractIds: string[]) => void;
}

function formatCountdown(targetMs: number, nowMs: number): string {
  const diff = Math.max(0, targetMs - nowMs);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (days > 0) return `${days}д ${hours}ч`;
  if (hours > 0) return `${hours}ч ${mins}м`;
  return `${mins}м`;
}

function formatUSDT(v: number): string {
  return v.toLocaleString('ru-RU');
}

function SlotCardImpl({
  slot,
  unlocked,
  owned = [],
  unlockAt,
  unlockAtMsk,
  showCountdown = false,
  onTapDetails,
  onTapBuy,
  onTapPC,
}: SlotCardProps) {
  const { t } = useTranslation();

  // Live countdown тикает раз в минуту — для нод месяца 2.
  // Открытие по 00:00 MSK 1-го числа, точность до минуты — норм.
  const targetMs = unlockAt ? Date.parse(unlockAt) : NaN;
  const hasLiveTimer = showCountdown && Number.isFinite(targetMs);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!hasLiveTimer) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [hasLiveTimer]);
  const countdown = hasLiveTimer ? formatCountdown(targetMs, nowMs) : null;
  const ownedCount = owned.length;
  const hasOwned = ownedCount > 0;

  // Derived state — preserves backwards compat with SlotState
  const state: SlotState = !unlocked
    ? 'locked'
    : hasOwned
      ? 'active'
      : 'open';
  const disabled = state === 'locked';

  // Aggregate progress across every owned instance
  const perSlotCap = slot.amount * 2.5;
  const totalCap = perSlotCap * Math.max(1, ownedCount);
  const totalEarned = owned.reduce((sum, o) => sum + o.earned, 0);
  const progressPct = hasOwned
    ? Math.min(100, (totalEarned / totalCap) * 100)
    : 0;

  // PC status
  const hasAnyPC = owned.some(o => o.plasmaCoreActive);
  const hasSlotWithoutPC = owned.some(o => !o.plasmaCoreActive);

  // Accent colour — PC slots get plasma-cyan tint
  const hue = slot.hue;
  const accent = `hsl(${hue} 80% 65%)`;
  const accentSoft = `hsl(${hue} 70% 55% / 0.18)`;
  const accentGlow = hasAnyPC
    ? `hsl(${hue} 80% 60% / 0.45), 0 0 40px -8px var(--color-plasma-500)`
    : `hsl(${hue} 80% 60% / 0.45)`;

  const sounds = useUISounds();
  // Whether to show the buy CTA at the bottom
  const canBuy = unlocked && Boolean(onTapBuy);

  // Whether the whole card body is interactive (details OR deposit)
  const rawBodyAction = hasOwned ? onTapDetails : canBuy ? onTapBuy : undefined;
  const bodyAction = rawBodyAction
    ? () => {
        sounds.slotCardTap();
        rawBodyAction();
      }
    : undefined;

  return (
    <div
      className="lg-surface lg-sheen relative w-full p-3 rounded-[var(--radius-card)] overflow-hidden"
      style={{
        opacity: disabled ? 0.55 : 1,
        filter: disabled ? 'saturate(0.3)' : undefined,
      }}
    >
      {/* Background glow accent — only for unlocked slots */}
      {!disabled && (
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accentGlow} 0%, transparent 65%)`,
            opacity: state === 'active' ? 0.85 : 0.55,
          }}
        />
      )}

      {/* ─── TOP: icon + details ─── */}
      <button
        type="button"
        onClick={bodyAction}
        disabled={!bodyAction}
        data-no-sound="true"
        className="relative w-full text-left flex items-start gap-3 transition-transform active:scale-[0.985]"
        style={{ cursor: bodyAction ? 'pointer' : 'default' }}
      >
        {/* Big icon */}
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-[22px] relative"
          style={{
            width: 96,
            height: 96,
            background: accentSoft,
            border: `1.5px solid ${accent}`,
            boxShadow: `0 0 56px -6px ${accentGlow}, inset 0 1px 0 0 rgba(255,255,255,0.28)`,
          }}
        >
          <img
            src={slot.icon}
            alt={slot.name}
            width={64}
            height={64}
            style={{ filter: `drop-shadow(0 0 16px ${accentGlow})` }}
          />
          {/* Multi-instance counter badge */}
          {ownedCount > 1 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[26px] h-[26px] rounded-full flex items-center justify-center font-mono font-extrabold text-[12px] tabular-nums text-white px-1.5"
              style={{
                background: `linear-gradient(180deg, ${accent}, color-mix(in oklab, ${accent} 60%, #000))`,
                border: '1.5px solid var(--bg-base)',
                boxShadow: `0 6px 18px -4px ${accentGlow}`,
              }}
            >
              ×{ownedCount}
            </span>
          )}
        </div>

        {/* Body — row: text column | pills column */}
        <div className="flex-1 min-w-0 flex items-start gap-2">
          {/* Text column */}
          <div className="flex-1 min-w-0 flex flex-col">
            <p className="text-[9px] max-[400px]:text-[8px] uppercase tracking-[0.18em] max-[400px]:tracking-[0.12em] text-[var(--text-muted)] font-mono leading-tight">
              {t('slotCard.contractLine', { month: slot.unlockMonth })}
            </p>
            <h3 className="font-display text-[18px] max-[400px]:text-[16px] font-extrabold text-[var(--text-primary)] tracking-tight leading-tight mt-0.5 truncate">
              {slot.name}
            </h3>
            <p className="text-[11px] max-[400px]:text-[10px] text-[var(--text-secondary)] mt-0.5 line-clamp-2 leading-snug">
              {slot.tagline}
            </p>
            {/* Amount + rate on one line */}
            <div className="mt-2 flex items-baseline justify-between gap-2 flex-wrap">
              <div className="flex items-baseline gap-1.5 min-w-0">
                <span className="font-display text-[18px] max-[400px]:text-[16px] font-extrabold text-[var(--text-primary)] tabular-nums tracking-tight">
                  {formatUSDT(slot.amount)}
                </span>
                <span className="text-[11px] max-[400px]:text-[10px] text-[var(--text-muted)] font-bold">USD₮</span>
                {ownedCount > 1 && (
                  <span className="text-[10px] max-[400px]:text-[9px] text-[var(--text-muted)] font-mono whitespace-nowrap">
                    ×{ownedCount}={formatUSDT(slot.amount * ownedCount)}
                  </span>
                )}
              </div>
              <span
                className="text-[10px] max-[400px]:text-[9px] font-mono font-extrabold tabular-nums whitespace-nowrap flex-shrink-0"
                style={{ color: accent }}
              >
                {t('slotCard.monthlyRange', { rate: slot.monthlyRate })}
              </span>
            </div>
          </div>

          {/* Pills column — right edge, stacked */}
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {state === 'active' && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[7.5px] max-[400px]:text-[6.8px] font-mono font-extrabold uppercase tracking-[0.08em]"
                style={{
                  background: 'color-mix(in oklab, var(--status-positive) 18%, transparent)',
                  color: 'var(--status-positive)',
                  border: '1px solid color-mix(in oklab, var(--status-positive) 40%, transparent)',
                }}
              >
                <Check size={9} strokeWidth={3} />
                {t('slotCard.statusActive')}
              </span>
            )}
            {hasAnyPC && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[7.5px] max-[400px]:text-[6.8px] font-mono font-extrabold uppercase tracking-[0.08em]"
                style={{
                  background: 'color-mix(in oklab, var(--color-plasma-400) 18%, transparent)',
                  color: 'var(--color-plasma-400)',
                  border: '1px solid color-mix(in oklab, var(--color-plasma-400) 40%, transparent)',
                }}
              >
                <Zap size={9} strokeWidth={3} />
                Plasma Core
              </span>
            )}
            {state === 'open' && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[7.5px] max-[400px]:text-[6.8px] font-mono font-extrabold uppercase tracking-[0.08em]"
                style={{
                  background: accentSoft,
                  color: accent,
                  border: `1px solid ${accent}`,
                }}
              >
                {t('slotCard.statusOpen')}
              </span>
            )}
            {state === 'locked' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[7.5px] font-mono font-bold uppercase tracking-[0.08em] text-[var(--text-disabled)] border border-[var(--glass-stroke)]">
                {t('slotCard.statusLocked')}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* ─── Progress row for active slots ─── */}
      {state === 'active' && (
        <div className="mt-4 relative">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono font-bold">
              {t('slotCard.incomeCap')}
              {ownedCount > 1 && <span className="ml-1">{t('slotCard.sumCount', { count: ownedCount })}</span>}
            </span>
            <span className="font-mono text-[10px] font-extrabold text-[var(--text-secondary)] tabular-nums">
              {totalEarned > 0 ? parseFloat(totalEarned.toFixed(3)) : 0} / {totalCap}
            </span>
          </div>
          <div
            className="h-[5px] rounded-full overflow-hidden"
            style={{ background: 'var(--glass-stroke)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${progressPct}%`,
                background: `linear-gradient(90deg, ${accent}, color-mix(in oklab, ${accent} 60%, var(--accent-secondary)))`,
                boxShadow: `0 0 10px ${accentGlow}`,
                transition: 'width 0.6s cubic-bezier(0.32, 0.72, 0, 1)',
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            {ownedCount > 1 && onTapDetails ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  sounds.slotCardTap();
                  onTapDetails();
                }}
                className="text-[9px] font-mono text-[var(--text-disabled)] uppercase tracking-[0.14em] transition-colors active:text-[var(--text-secondary)] cursor-pointer"
              >
                {t('slotCard.countContractsDetails', { count: ownedCount })}
              </button>
            ) : (
              <span className="text-[9px] font-mono text-[var(--text-disabled)] uppercase tracking-[0.14em]">
                {ownedCount === 1
                  ? t('slotCard.daysLive', { days: owned[0].daysActive })
                  : t('slotCard.countContractsDetails', { count: ownedCount })}
              </span>
            )}
            <span
              className="text-[9px] font-mono font-extrabold"
              style={{ color: accent }}
            >
              {progressPct.toFixed(1)}%
            </span>
          </div>
        </div>
      )}

      {/* ─── Buy CTA ─── */}
      {state === 'open' && (
        <button
          type="button"
          onClick={onTapBuy}
          className="mt-3.5 w-full flex items-center justify-between gap-2 transition-transform active:scale-[0.98]"
        >
          <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-[0.14em]">
            {t('slotCard.dailyRateCap', { rate: slot.dailyRate })}
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-display font-extrabold uppercase tracking-[0.14em]"
            style={{
              background: accent,
              color: '#0a0a0f',
              boxShadow: `0 6px 18px -6px ${accent}, inset 0 1px 0 0 rgba(255,255,255,0.35)`,
            }}
          >
            <Flame size={11} strokeWidth={2.8} />
            {t('slotCard.buyBtn')}
          </span>
        </button>
      )}

      {state === 'active' && (
        <div className="mt-3 flex gap-2">
          {/* PC CTA — если есть контракт без PC */}
          {hasSlotWithoutPC && onTapPC && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const ids = owned.filter(o => !o.plasmaCoreActive).map(o => o.contractId);
                onTapPC(ids);
              }}
              className="flex-1 rounded-2xl py-2 flex items-center justify-center gap-1.5 font-mono font-extrabold uppercase tracking-[0.14em] text-[10px] transition-all active:scale-[0.98]"
              style={{
                background: 'linear-gradient(180deg, color-mix(in oklab, var(--color-plasma-400) 22%, transparent) 0%, color-mix(in oklab, var(--color-plasma-400) 6%, transparent) 100%)',
                border: '1px solid color-mix(in oklab, var(--color-plasma-400) 45%, transparent)',
                color: 'var(--color-plasma-400)',
                boxShadow: '0 0 20px -6px var(--color-plasma-500)',
              }}
            >
              <Zap size={11} strokeWidth={2.6} />
              Plasma Core
            </button>
          )}
          {/* Buy more CTA */}
          {canBuy && (
            <button
              type="button"
              onClick={onTapBuy}
              className="flex-1 rounded-2xl py-2 flex items-center justify-center gap-1.5 font-mono font-extrabold uppercase tracking-[0.14em] text-[10px] transition-all active:scale-[0.98]"
              style={{
                background: `linear-gradient(180deg, color-mix(in oklab, ${accent} 22%, transparent) 0%, color-mix(in oklab, ${accent} 6%, transparent) 100%)`,
                border: `1px solid color-mix(in oklab, ${accent} 45%, transparent)`,
                color: accent,
              }}
            >
              <Plus size={11} strokeWidth={2.6} />
              {t('slotCard.more1')}
            </button>
          )}
        </div>
      )}

      {state === 'locked' && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] font-mono text-[var(--text-disabled)] uppercase tracking-[0.14em]">
            {t('slotCard.lineM', { month: slot.unlockMonth })}
          </span>
          {countdown ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[var(--text-secondary)] uppercase tracking-[0.14em] tabular-nums">
              <Clock size={10} strokeWidth={2.4} />
              {t('slotCard.countdownTo', { time: countdown })}
            </span>
          ) : unlockAtMsk ? (
            <span className="text-[10px] font-mono text-[var(--text-disabled)] uppercase tracking-[0.14em]">
              {t('slotCard.opensOn', { date: unlockAtMsk })}
            </span>
          ) : (
            <span className="text-[10px] font-mono text-[var(--text-disabled)] uppercase tracking-[0.14em]">
              {t('slotCard.soon')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export const SlotCard = memo(SlotCardImpl);
